import React, { FC, useState } from 'react';

import { Select, TextInput } from '@gravity-ui/uikit';
import { getHostsForSuggest } from 'client/api';
import { useDebouncedCallback } from 'use-debounce';

import styles from './HostsSelect.module.scss';

interface Props {
    onChange: (value: string) => void;
    disabled: boolean;

    error?: string;
    value?: string;
}

export const HostsSelect: FC<Props> = ({ value, onChange, disabled, error }) => {
    const [cache, setCache] = useState<Record<string, string[]>>({});
    const [suggests, setSuggests] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [internalValue, setInternalValue] = useState(value);

    const getItems = async (value: string) => {
        if (cache[value]) {
            return cache[value];
        }
        let hosts: string[] = [];

        try {
            hosts = await getHostsForSuggest({ query: value });
            setCache({ ...cache, [value]: hosts });
        } catch (e) {
            console.error(e);
        }
        return hosts;
    };

    const getItemsDebounced = useDebouncedCallback(async (val: string) => {
        const items = await getItems(val);
        setSuggests(items);
    }, 400);
    const onChangeDebounced = useDebouncedCallback(onChange, 400);

    const handleClose = () => {
        setIsOpen(false);
        setSuggests([]);
    };

    const handleChange = (val: string) => {
        if (val === internalValue) return;
        setIsOpen(true);
        setInternalValue(val);
        onChangeDebounced(val);
        getItemsDebounced(val);
    };

    const handleUpdate = (values: string[]) => {
        setInternalValue(values[0]);
        onChange(values[0]);
        handleClose();
    };

    return (
        <Select
            className={styles.container}
            renderControl={({ ref }) => {
                return (
                    <TextInput
                        ref={ref}
                        value={internalValue}
                        onChange={(e) => handleChange(e.target.value)}
                        size="xl"
                        disabled={disabled}
                    />
                );
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={handleClose}
            onUpdate={handleUpdate}
            open={isOpen && !!suggests.length}
            size="xl"
            error={error}
            disabled={disabled}
        >
            {suggests.map((el) => (
                <Select.Option key={el} value={el}>
                    {el}
                </Select.Option>
            ))}
        </Select>
    );
};
